"use client";

import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import { ArrowLeft, ArrowRight, Pause, Play } from "lucide-react";
import * as React from "react";
import { Button } from "../../elements/button/button";
import { cn } from "../../lib/utils";

import {
  CardGeneric,
  type CardGenericProps,
} from "../card-generic/card-generic";

type CarouselApi = UseEmblaCarouselType[1];

const MIN_ITEMS_FOR_SCROLL = 3;
const FULL_PERCENT = 100;
const DEFAULT_AUTOPLAY_DELAY = 5000;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  scrollTo: (index: number) => void;
  selectedIndex: number;
  scrollSnapCount: number;
  canScrollPrev: boolean;
  canScrollNext: boolean;
};

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);
  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }
  return context;
}

export interface CarouselConfigurationProps {
  behavior?: "slider" | "carousel";
  autoplay?: boolean;
  autoplayDelay?: number;
  navigationLayout?: "dots" | "bars";
  showNavigationArrows?: boolean;
}

export interface CarouselNavigationLabels {
  previousSlideText: string;
  nextSlideText: string;
}

interface CarouselProps
  extends CarouselConfigurationProps,
    CarouselNavigationLabels {
  title?: string;
  cardTitle?: string;
  children?: React.ReactNode;
  button?: { text: string; href: string };
  items?: CardGenericProps[];
  dragFree?: boolean;
  disableDragWhenNotScrollable?: boolean;
  showProgressBar?: boolean;
  progressBarClassName?: string;
  progressTrackClassName?: string;
  progressIndicatorClassName?: string;
  navigationPlacement?: "footer" | "sides";
  sideNavigationPosition?: "inside" | "outside";
  navigationMinItems?: number;
  showPagination?: boolean;
  interactiveProgressBar?: boolean;
  dotClassName?: string;
  dotButtonClassName?: string;
  activeDotClassName?: string;
  dotNavigationClassName?: string;
  slideNavigationText?: string;
  goToSlideText?: string;
  autoplayControlClassName?: string;
  pauseAutoplayText?: string;
  resumeAutoplayText?: string;
  showAutoplayControl?: boolean;
  viewportClassName?: string;
  trackClassName?: string;
  wheelGestures?: boolean;
}

function Carousel(props: Readonly<CarouselProps>) {
  const [isScrollable, setIsScrollable] = React.useState(
    !props.disableDragWhenNotScrollable
  );

  const autoplayPlugin = React.useMemo(
    () =>
      Autoplay({
        delay: props.autoplayDelay ?? DEFAULT_AUTOPLAY_DELAY,
        playOnInit: false,
        stopOnFocusIn: true,
        stopOnInteraction: true,
        stopOnLastSnap: props.behavior !== "carousel",
        stopOnMouseEnter: true,
      }),
    [props.autoplayDelay, props.behavior]
  );
  const plugins = React.useMemo(
    () => (props.autoplay ? [autoplayPlugin] : []),
    [autoplayPlugin, props.autoplay]
  );
  const [carouselRef, api] = useEmblaCarousel(
    {
      align: "start",
      dragFree: props.dragFree,
      loop: props.behavior === "carousel",
      watchDrag: props.disableDragWhenNotScrollable ? isScrollable : true,
    },
    plugins
  );
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [scrollSnapCount, setScrollSnapCount] = React.useState(0);
  const [isAutoplayPaused, setIsAutoplayPaused] = React.useState(true);
  const autoplayManuallyPausedRef = React.useRef(false);

  const stopAutoplay = React.useCallback(() => {
    autoplayManuallyPausedRef.current = true;
    autoplayPlugin.stop();
    setIsAutoplayPaused(true);
  }, [autoplayPlugin]);

  const toggleAutoplay = React.useCallback(() => {
    if (isAutoplayPaused) {
      autoplayManuallyPausedRef.current = false;
      autoplayPlugin.play();
      setIsAutoplayPaused(false);
      return;
    }

    stopAutoplay();
  }, [autoplayPlugin, isAutoplayPaused, stopAutoplay]);

  const onSelect = React.useCallback(
    (api: CarouselApi) => {
      if (!api) {
        return;
      }
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());

      if (props.showPagination) {
        setSelectedIndex(api.selectedScrollSnap());
        setScrollSnapCount(api.scrollSnapList().length);
      }
    },
    [props.showPagination]
  );

  const scrollPrev = React.useCallback(() => {
    stopAutoplay();
    api?.scrollPrev();
  }, [api, stopAutoplay]);

  const scrollNext = React.useCallback(() => {
    stopAutoplay();
    api?.scrollNext();
  }, [api, stopAutoplay]);

  const scrollTo = React.useCallback(
    (index: number) => {
      stopAutoplay();
      api?.scrollTo(index);
    },
    [api, stopAutoplay]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        scrollNext();
      }
    },
    [scrollPrev, scrollNext]
  );

  React.useEffect(() => {
    if (!api) {
      return;
    }

    const updateScrollable = () => {
      if (!props.disableDragWhenNotScrollable) {
        return;
      }

      const nextScrollable = api.canScrollPrev() || api.canScrollNext();
      setIsScrollable((previous) =>
        previous === nextScrollable ? previous : nextScrollable
      );
    };

    onSelect(api);
    updateScrollable();
    api.on("reInit", onSelect);
    api.on("select", onSelect);
    api.on("reInit", updateScrollable);
    api.on("select", updateScrollable);

    return () => {
      api?.off("select", onSelect);
      api?.off("reInit", onSelect);
      api?.off("select", updateScrollable);
      api?.off("reInit", updateScrollable);
    };
  }, [api, onSelect, props.disableDragWhenNotScrollable]);

  React.useEffect(() => {
    if (!(api && props.autoplay)) {
      return;
    }

    const handleAutoplayPlay = () => setIsAutoplayPaused(false);
    const handleAutoplayStop = () => setIsAutoplayPaused(true);

    api.on("autoplay:play", handleAutoplayPlay);
    api.on("autoplay:stop", handleAutoplayStop);

    return () => {
      api.off("autoplay:play", handleAutoplayPlay);
      api.off("autoplay:stop", handleAutoplayStop);
    };
  }, [api, props.autoplay]);

  React.useEffect(() => {
    if (!(api && props.autoplay)) {
      return;
    }

    const reducedMotion =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(REDUCED_MOTION_QUERY).matches;

    if (reducedMotion) {
      autoplayPlugin.stop();
      setIsAutoplayPaused(true);
      return;
    }

    if (autoplayManuallyPausedRef.current) {
      autoplayPlugin.stop();
      setIsAutoplayPaused(true);
      return;
    }

    autoplayPlugin.play();
    setIsAutoplayPaused(false);

    return () => {
      autoplayPlugin.stop();
    };
  }, [api, autoplayPlugin, props.autoplay]);

  const outerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!(props.wheelGestures && outerRef.current)) {
      return;
    }

    const el = outerRef.current;
    let accumulated = 0;
    let raf: ReturnType<typeof requestAnimationFrame>;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) {
        return;
      }

      e.preventDefault();

      accumulated += e.deltaX;
      cancelAnimationFrame(raf);

      raf = requestAnimationFrame(() => {
        if (accumulated > 0) {
          api?.scrollNext();
        } else {
          api?.scrollPrev();
        }

        accumulated = 0;
      });
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      cancelAnimationFrame(raf);
    };
  }, [api, props.wheelGestures]);

  const contextValue = React.useMemo(
    () => ({
      carouselRef,
      api,
      scrollPrev,
      scrollNext,
      scrollTo,
      selectedIndex,
      scrollSnapCount,
      canScrollPrev,
      canScrollNext,
    }),
    [
      carouselRef,
      api,
      scrollPrev,
      scrollNext,
      scrollTo,
      selectedIndex,
      scrollSnapCount,
      canScrollPrev,
      canScrollNext,
    ]
  );

  const showNavigationArrows =
    props.showNavigationArrows === undefined
      ? true
      : props.showNavigationArrows;
  const shouldShowNavigation = showNavigationArrows || !!props.button;
  const itemCount = props.items
    ? props.items.length
    : React.Children.count(props.children);
  const navigationMinItems = props.navigationMinItems ?? MIN_ITEMS_FOR_SCROLL;
  const hasEnoughItemsForNavigation = itemCount >= navigationMinItems;
  const navigationPlacement = props.navigationPlacement ?? "footer";
  const sideNavigation =
    hasEnoughItemsForNavigation &&
    showNavigationArrows &&
    navigationPlacement === "sides";
  const sideNavigationOutside = props.sideNavigationPosition === "outside";

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        className="relative w-full min-w-0"
        onKeyDownCapture={handleKeyDown}
        ref={outerRef}
      >
        {/* Carousel viewport or children */}
        <div
          className={cn("overflow-hidden px-px", props.viewportClassName)}
          ref={carouselRef}
        >
          <div className={cn("-ml-2 flex", props.trackClassName)}>
            {props.items
              ? props.items.map((item, index) => (
                  <CarouselItem
                    key={`${item.title}-${index}`}
                    variant={item.variant}
                  >
                    <CardGeneric {...item} />
                  </CarouselItem>
                ))
              : props.children}
          </div>
        </div>

        {sideNavigation && (
          <>
            <CarouselNavigationButton
              className={cn(
                "-translate-y-1/2 absolute top-1/2 left-2 z-10",
                sideNavigationOutside && "lg:-left-12 xl:-left-16"
              )}
              disabled={!canScrollPrev}
              label={props.previousSlideText}
              onClick={scrollPrev}
              side="previous"
              size="icon"
            />
            <CarouselNavigationButton
              className={cn(
                "-translate-y-1/2 absolute top-1/2 right-2 z-10",
                sideNavigationOutside && "lg:-right-12 xl:-right-16"
              )}
              disabled={!canScrollNext}
              label={props.nextSlideText}
              onClick={scrollNext}
              side="next"
              size="icon"
            />
          </>
        )}

        {/* Navigation row: arrows on left, "See all" on right */}
        {hasEnoughItemsForNavigation && shouldShowNavigation && (
          <div className="mt-5 flex items-center justify-between">
            {showNavigationArrows && navigationPlacement === "footer" ? (
              <div className="flex items-center gap-1">
                <CarouselNavigationButton
                  disabled={!canScrollPrev}
                  label={props.previousSlideText}
                  onClick={scrollPrev}
                  side="previous"
                />
                <CarouselNavigationButton
                  disabled={!canScrollNext}
                  label={props.nextSlideText}
                  onClick={scrollNext}
                  side="next"
                />
              </div>
            ) : (
              <div />
            )}
            {props.button && (
              <Button
                href={props.button.href}
                size="default"
                variant="secondary"
              >
                {props.button.text}
              </Button>
            )}
          </div>
        )}

        {props.showPagination &&
          hasEnoughItemsForNavigation &&
          (props.navigationLayout === "bars" ? (
            <CarouselProgress
              className={props.progressBarClassName}
              indicatorClassName={props.progressIndicatorClassName}
              interactive
              trackClassName={props.progressTrackClassName}
            />
          ) : (
            <CarouselDots
              activeDotClassName={props.activeDotClassName}
              autoplayControl={
                props.autoplay && props.showAutoplayControl
                  ? {
                      buttonClassName: props.autoplayControlClassName,
                      isPaused: isAutoplayPaused,
                      pauseText: props.pauseAutoplayText,
                      resumeText: props.resumeAutoplayText,
                      toggle: toggleAutoplay,
                    }
                  : undefined
              }
              buttonClassName={props.dotButtonClassName}
              dotClassName={props.dotClassName}
              goToSlideText={props.goToSlideText}
              navigationClassName={props.dotNavigationClassName}
              navigationText={props.slideNavigationText}
            />
          ))}

        {props.showProgressBar &&
          !(props.showPagination && props.navigationLayout === "bars") && (
            <CarouselProgress
              className={props.progressBarClassName}
              indicatorClassName={props.progressIndicatorClassName}
              interactive={props.interactiveProgressBar}
              trackClassName={props.progressTrackClassName}
            />
          )}
      </div>
    </CarouselContext.Provider>
  );
}

function CarouselNavigationButton({
  className,
  disabled,
  label,
  onClick,
  size = "icon",
  side,
}: Readonly<{
  className?: string;
  disabled: boolean;
  label: string;
  onClick: () => void;
  size?: "icon" | "icon-sm";
  side: "previous" | "next";
}>) {
  return (
    <Button
      className={className}
      data-carousel-control={side === "previous" ? "prev" : "next"}
      disabled={disabled}
      onClick={onClick}
      size={size}
      variant="outline"
    >
      {side === "previous" ? (
        <ArrowLeft className="size-4" />
      ) : (
        <ArrowRight className="size-4" />
      )}
      <span className="sr-only">{label}</span>
    </Button>
  );
}

function CarouselDots({
  dotClassName,
  activeDotClassName,
  autoplayControl,
  buttonClassName,
  goToSlideText = "Go to slide",
  navigationClassName,
  navigationText = "Slide navigation",
}: Readonly<{
  dotClassName?: string;
  activeDotClassName?: string;
  autoplayControl?: {
    buttonClassName?: string;
    isPaused: boolean;
    pauseText?: string;
    resumeText?: string;
    toggle: () => void;
  };
  buttonClassName?: string;
  goToSlideText?: string;
  navigationClassName?: string;
  navigationText?: string;
}>) {
  const { scrollSnapCount, selectedIndex, scrollTo } = useCarousel();

  if (scrollSnapCount < 2) {
    return null;
  }

  return (
    <nav
      aria-label={navigationText}
      className={cn("mt-8 flex justify-center gap-2", navigationClassName)}
    >
      {autoplayControl && (
        <button
          aria-label={
            autoplayControl.isPaused
              ? (autoplayControl.resumeText ?? "Resume slide rotation")
              : (autoplayControl.pauseText ?? "Pause slide rotation")
          }
          className={cn(
            "grid size-6 place-items-center rounded-full text-black",
            "focus-visible:outline-2 focus-visible:outline-current focus-visible:outline-offset-2",
            autoplayControl.buttonClassName
          )}
          onClick={autoplayControl.toggle}
          type="button"
        >
          {autoplayControl.isPaused ? (
            <Play aria-hidden="true" className="size-2.5 fill-current" />
          ) : (
            <Pause aria-hidden="true" className="size-2.5 fill-current" />
          )}
        </button>
      )}
      {Array.from({ length: scrollSnapCount }, (_, index) => (
        <button
          aria-current={selectedIndex === index ? "true" : undefined}
          aria-label={`${goToSlideText} ${index + 1}`}
          className={cn(
            "grid size-6 place-items-center rounded-full text-black",
            "focus-visible:outline-2 focus-visible:outline-current focus-visible:outline-offset-2",
            buttonClassName
          )}
          data-carousel-control="dot"
          // biome-ignore lint/suspicious/noArrayIndexKey: slide positions are stable within the carousel.
          key={index}
          onClick={() => scrollTo(index)}
          type="button"
        >
          <span
            aria-hidden="true"
            className={cn(
              "size-2 rounded-full bg-black/30 transition-colors",
              dotClassName,
              selectedIndex === index && "bg-black",
              selectedIndex === index && activeDotClassName
            )}
          />
        </button>
      ))}
    </nav>
  );
}

function CarouselProgress({
  className,
  trackClassName,
  indicatorClassName,
  interactive = false,
}: Readonly<{
  className?: string;
  trackClassName?: string;
  indicatorClassName?: string;
  interactive?: boolean;
}>) {
  const { api, canScrollPrev, canScrollNext, scrollSnapCount, scrollTo } =
    useCarousel();
  const [thumbWidthPercent, setThumbWidthPercent] =
    React.useState(FULL_PERCENT);
  const [thumbTranslatePercent, setThumbTranslatePercent] = React.useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    const updateProgress = () => {
      const viewportNode = api.rootNode();
      const containerNode = api.containerNode();
      const viewportWidth = viewportNode.clientWidth;
      const contentWidth = containerNode.scrollWidth;

      if (!(viewportWidth && contentWidth)) {
        setThumbWidthPercent(FULL_PERCENT);
        setThumbTranslatePercent(0);
        return;
      }

      const widthRatio = Math.min(1, viewportWidth / contentWidth);
      const computedThumbWidth = widthRatio * FULL_PERCENT;
      const clampedThumbWidth = Math.min(FULL_PERCENT, computedThumbWidth);
      const maxTranslate = FULL_PERCENT - clampedThumbWidth;
      const clampedProgress = Math.min(1, Math.max(0, api.scrollProgress()));

      setThumbWidthPercent(clampedThumbWidth);
      setThumbTranslatePercent(clampedProgress * maxTranslate);
    };

    updateProgress();
    api.on("scroll", updateProgress);
    api.on("select", updateProgress);
    api.on("reInit", updateProgress);

    return () => {
      api.off("scroll", updateProgress);
      api.off("select", updateProgress);
      api.off("reInit", updateProgress);
    };
  }, [api]);

  const isSlideable = canScrollPrev || canScrollNext;

  if (!isSlideable) {
    return null;
  }

  const handleProgressClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!(interactive && scrollSnapCount > 1)) {
      return;
    }

    const bounds = event.currentTarget.getBoundingClientRect();
    const progress = Math.min(
      1,
      Math.max(0, (event.clientX - bounds.left) / bounds.width)
    );
    const index = Math.round(progress * (scrollSnapCount - 1));
    scrollTo(index);
  };

  const trackClasses = cn(
    "relative h-[2px] w-full max-w-[360px] bg-black/15",
    interactive && "cursor-pointer",
    trackClassName
  );
  const indicator = (
    <div
      className={cn(
        "pointer-events-none absolute top-0 left-0 h-[2px] bg-black",
        indicatorClassName
      )}
      data-testid="carousel-progress-indicator"
      style={{
        left: `${thumbTranslatePercent}%`,
        width: `${thumbWidthPercent}%`,
      }}
    />
  );

  return (
    <div
      className={cn("mt-15 flex justify-center", className)}
      data-testid="carousel-progress"
    >
      {interactive ? (
        <button
          aria-label="Go to slide"
          className={trackClasses}
          data-testid="carousel-progress-track"
          onClick={handleProgressClick}
          type="button"
        >
          {indicator}
        </button>
      ) : (
        <div className={trackClasses} data-testid="carousel-progress-track">
          {indicator}
        </div>
      )}
    </div>
  );
}

function CarouselItem({
  children,
  variant,
}: Readonly<{
  children: React.ReactNode;
  variant?: CardGenericProps["variant"];
}>) {
  const isPrimaryLarge = variant === "primary-large";
  return (
    <div
      className={
        isPrimaryLarge
          ? "h-generic-card-height w-full min-w-0 max-w-generic-card-width shrink-0 grow-0 basis-full pr-4 pl-2 md:basis-1/2 lg:basis-1/2"
          : "min-w-0 shrink-0 grow-0 basis-4/5 pr-4 pl-2 md:basis-1/2 lg:basis-1/4"
      }
    >
      {children}
    </div>
  );
}

export {
  type CarouselApi,
  type CarouselProps,
  Carousel,
  CarouselItem,
  useCarousel,
};
